/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { HFlow, isEmpty, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import { useAcompanhamentoPreNatalVacinacaoQuery } from 'graphql/hooks.generated'
import { Fragment } from 'react'

import { AcompanhamentoCard } from '../../components/AcompanhamentoCard'
import { AcompanhamentoHeaderLink } from '../../components/AcompanhamentoHeaderLink'
import { IconLabelPreNatal } from './IconLabelPreNatal'
import { ImunobiologicoSection } from './ImunobiologicoSection'

interface VacinacaoGestacaoCardProps {
  dataInicioGestacao: Date
  vacinacaoPath: string
  loading: boolean
  prontuarioId: string
}

export function VacinacaoGestacaoCard(props: Readonly<VacinacaoGestacaoCardProps>) {
  const { dataInicioGestacao, vacinacaoPath, loading, prontuarioId } = props
  const theme = useTheme()
  const styles = createStyles(theme)

  const { data, loading: vacinacaoLoading } = useAcompanhamentoPreNatalVacinacaoQuery({
    variables: {
      input: {
        prontuarioId: prontuarioId,
        dataInicioGestacao: dataInicioGestacao,
      },
    },
    skip: !prontuarioId || !dataInicioGestacao,
    fetchPolicy: 'cache-and-network',
  })

  const vacinasAplicadas = data?.acompanhamentoPreNatalVacinacao?.vacinasAplicadas || []
  const vacinasAprazadas = data?.acompanhamentoPreNatalVacinacao?.vacinasAprazadas || []
  const ultimaVacinaAplicadaCovid = data?.acompanhamentoPreNatalVacinacao?.ultimaVacinaAplicadaCovid
  const ultimaVacinaAprazadaCovid = data?.acompanhamentoPreNatalVacinacao?.ultimaVacinaAprazadaCovid

  const hasVacinasAprazadas = ultimaVacinaAprazadaCovid || !isEmpty(vacinasAprazadas)

  return (
    <AcompanhamentoCard
      id='vacinacao'
      title='Vacinação'
      loading={loading || vacinacaoLoading}
      placeholder='Nenhuma vacinação registrada'
      headerContent={<AcompanhamentoHeaderLink title='Visualizar calendário' path={vacinacaoPath} />}
      justifyHeaderContent='space-between'
    >
      <VFlow vSpacing={0}>
        <Box style={styles.box}>
          <IconLabelPreNatal icon='needleFilled' iconText='Última aplicação' />
        </Box>
        <HFlow hSpacing={0.5} style={styles.bodyContent}>
          {vacinasAplicadas.map((item, index) => (
            <ImunobiologicoSection key={index} registroVacinacao={item} />
          ))}
          {ultimaVacinaAplicadaCovid && <ImunobiologicoSection registroVacinacao={ultimaVacinaAplicadaCovid} isCovid />}
        </HFlow>
        <Box style={styles.box}>
          <IconLabelPreNatal icon='calendarFilled' iconText='Aplicações aprazadas' />
        </Box>
        <HFlow style={styles.bodyContent}>
          {hasVacinasAprazadas ? (
            <Fragment>
              {!isEmpty(vacinasAprazadas) &&
                vacinasAprazadas?.map((item, index) => (
                  <ImunobiologicoSection key={index} registroVacinacao={item} isAprazada />
                ))}
              {ultimaVacinaAprazadaCovid && (
                <ImunobiologicoSection registroVacinacao={ultimaVacinaAprazadaCovid} isCovid isAprazada />
              )}
            </Fragment>
          ) : (
            <Text> Nenhuma vacina aprazada </Text>
          )}
        </HFlow>
      </VFlow>
    </AcompanhamentoCard>
  )
}

const createStyles = (theme: Theme) => ({
  bodyContent: css`
    padding: 0.5rem;
  `,
  box: css`
    margin: 0.5rem;
    padding: 0.5rem;
    border: 0;
    background: ${theme.pallete.gray.c90};
  `,
})
